import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw, AlertCircle, TrendingDown } from 'lucide-react';
import { apiService } from '../../services/apiService';

function MetricCard({ label, value, subtitle, color = 'text-white' }) {
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

export default function CSREffortTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.fetchDashboardView('CSREffort');
      setData(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-medium">Failed to load CSR effort data</p>
          <p className="text-red-400/70 text-sm">{error}</p>
        </div>
        <button onClick={loadData} className="ml-auto px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const latest = data.length > 0 ? data[data.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">CSR Effort</h2>
        </div>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Avg Review Time"
            value={formatDuration(latest.AvgReviewSeconds)}
            subtitle={`${latest.TotalOrders} orders in ${latest.Month}`}
            color="text-amber-400"
          />
          <MetricCard
            label="Avg Actions / Order"
            value={latest.AvgActions != null ? latest.AvgActions.toFixed(1) : '—'}
            subtitle="Clicks, selections, resolutions"
          />
          <MetricCard
            label="Avg Items Changed"
            value={latest.AvgItemsChanged != null ? latest.AvgItemsChanged.toFixed(1) : '—'}
            subtitle={`of ~${latest.AvgItemsPerOrder?.toFixed(0) || '?'} items/order`}
          />
          <MetricCard
            label="Avg Resolutions"
            value={latest.AvgResolutions != null ? latest.AvgResolutions.toFixed(1) : '—'}
            subtitle="UOM/price resolutions per order"
          />
        </div>
      )}

      {/* Monthly Trend Table */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="w-4 h-4 text-slate-400" />
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Trend</h3>
          <span className="text-xs text-slate-600 ml-2">Lower is better — AI accuracy improvements should reduce CSR effort</span>
        </div>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm py-4 text-center">No data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Month</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Orders</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Review Time</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Actions</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Items Changed</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Deleted</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Resolutions</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Items/Order</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-2 px-3 text-slate-300">{row.Month}</td>
                    <td className="py-2 px-3 text-slate-300">{row.TotalOrders}</td>
                    <td className="py-2 px-3 text-slate-300">{formatDuration(row.AvgReviewSeconds)}</td>
                    <td className="py-2 px-3 text-slate-300">{row.AvgActions?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-300">{row.AvgItemsChanged?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-300">{row.AvgItemsDeleted?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-300">{row.AvgResolutions?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-300">{row.AvgItemsPerOrder?.toFixed(0) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
